import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import StatusBadge from '../../components/common/StatusBadge';
import Pill from '../../components/common/Pill';
import CopiarTexto from '../../components/common/CopiarTexto';
import ConfirmarModal from '../../components/common/ConfirmarModal';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../contexts/AuthContext';
import * as eventosApi from '../../api/eventos';
import { formatDataHora } from '../../utils/date';
import { formatBytes, formatJson } from '../../utils/formato';

function Linha({ rotulo, children }) {
  return (
    <div className="dado-linha">
      <div className="dado-linha__rotulo">{rotulo}</div>
      <div className="dado-linha__valor">{children}</div>
    </div>
  );
}

export default function EventoDetalhe() {
  const { idEvento } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [evento, setEvento] = useState(null);
  const [erro, setErro] = useState('');
  const [aba, setAba] = useState('formatado');
  const [confirmando, setConfirmando] = useState(null);
  const [reenvioAberto, setReenvioAberto] = useState(false);
  const [urlReenvio, setUrlReenvio] = useState('');
  const [reenviando, setReenviando] = useState(false);
  const [resultadoReenvio, setResultadoReenvio] = useState(null);

  const carregar = useCallback(async () => {
    setErro('');
    try {
      const { data } = await eventosApi.obter(idEvento);
      setEvento(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar o evento.'));
    }
  }, [idEvento]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (erro && !evento) return <ErroCarregamento mensagem={erro} onRetry={carregar} />;
  if (!evento) return <LoadingSpinner />;

  const podeReenviar = user?.perfil === 'admin' || user?.perfil === 'operador';

  const reenviar = async () => {
    setReenviando(true);
    setResultadoReenvio(null);
    try {
      const { data } = await eventosApi.reenviar(idEvento, urlReenvio);
      setResultadoReenvio({ ok: true, texto: data.detalhe || 'Reenviado com sucesso.' });
      await carregar();
    } catch (err) {
      setResultadoReenvio({ ok: false, texto: extrairMensagemErro(err, 'Falha no reenvio.') });
    } finally {
      setReenviando(false);
    }
  };

  return (
    <div>
      <PageHeader
        titulo="Detalhe do evento"
        subtitulo={`Recebido em ${formatDataHora(evento.recebido_em)} · ${evento.duracao_ms} ms de processamento`}
        acoes={
          <>
            <button type="button" className="wh-btn wh-btn-outline" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left" /> Voltar
            </button>
            <CopiarTexto texto={evento.curl} rotulo="Copiar cURL" classe="wh-btn wh-btn-outline" />
            {podeReenviar && (
              <button
                type="button"
                className="wh-btn wh-btn-primary"
                onClick={() => {
                  setResultadoReenvio(null);
                  setReenvioAberto(true);
                }}
              >
                <i className="bi bi-arrow-up-right-circle" /> Reenviar
              </button>
            )}
            {user?.perfil === 'admin' && (
              <button
                type="button"
                className="wh-btn wh-btn-danger"
                onClick={() =>
                  setConfirmando({
                    titulo: 'Excluir evento',
                    mensagem: 'O evento e o histórico de encaminhamento dele somem do banco. Não há como desfazer.',
                    acao: () => eventosApi.excluir(idEvento).then(() => navigate('/eventos')),
                  })
                }
              >
                <i className="bi bi-trash" /> Excluir
              </button>
            )}
          </>
        }
      />

      <div className="row g-3">
        <div className="col-12 col-lg-5">
          <div className="card-soft mb-3">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Identificação</h2>
            </div>
            <div className="card-soft__body">
              <Linha rotulo="Status">
                <div className="d-flex gap-1 flex-wrap">
                  <StatusBadge status={evento.status} />
                  <StatusBadge status={evento.auth_resultado} />
                  {evento.duplicado && (
                    <Pill
                      tone="warning"
                      icon="bi-files"
                      title="Corpo idêntico a um evento já recebido neste endpoint dentro da janela de duplicidade — compare o hash do corpo abaixo."
                    >
                      conteúdo repetido
                    </Pill>
                  )}
                  {evento.truncado && (
                    <Pill tone="warning" icon="bi-scissors">
                      truncado
                    </Pill>
                  )}
                </div>
              </Linha>
              {evento.motivo_rejeicao && <Linha rotulo="Motivo da rejeição">{evento.motivo_rejeicao}</Linha>}
              <Linha rotulo="Sistema detectado">{evento.sistema_detectado || 'não identificado'}</Linha>
              <Linha rotulo="Tipo do evento">
                <span className="wh-mono">{evento.evento_tipo || '—'}</span>
              </Linha>
              <Linha rotulo="ID no provedor">
                <span className="wh-mono">{evento.id_externo || '—'}</span>
              </Linha>
              <Linha rotulo="Assinatura">
                {evento.assinatura_valida === null ? 'não verificada' : evento.assinatura_valida ? 'válida' : 'inválida'}
              </Linha>
              <Linha rotulo="Resposta devolvida">
                <span className="d-inline-flex align-items-center gap-2">
                  HTTP {evento.status_resposta}
                  {evento.encaminhado !== 'nao' && <StatusBadge status={evento.encaminhado} />}
                </span>
              </Linha>
            </div>
          </div>

          <div className="card-soft">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Requisição</h2>
            </div>
            <div className="card-soft__body">
              <Linha rotulo="Método e caminho">
                <span className="wh-mono">
                  {evento.metodo} {evento.caminho}
                </span>
              </Linha>
              {evento.subcaminho && (
                <Linha rotulo="Subcaminho">
                  <span className="wh-mono">{evento.subcaminho}</span>
                </Linha>
              )}
              {evento.query_string && (
                <Linha rotulo="Query string">
                  <span className="wh-mono">{evento.query_string}</span>
                </Linha>
              )}
              <Linha rotulo="IP de origem">
                <span className="wh-mono">{evento.ip_origem || '—'}</span>
              </Linha>
              <Linha rotulo="User-Agent">{evento.user_agent || '—'}</Linha>
              <Linha rotulo="Content-Type">
                <span className="wh-mono">{evento.content_type || '—'}</span>
              </Linha>
              <Linha rotulo="Tamanho">{formatBytes(evento.tamanho_bytes)}</Linha>
              <Linha rotulo="Hash do corpo (sha256)">
                <span className="wh-mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                  {evento.corpo_hash || '—'}
                </span>
              </Linha>
              <Linha rotulo="ID do evento">
                <span className="wh-mono" style={{ fontSize: 11 }}>
                  {evento.id_evento}
                </span>
              </Linha>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-7">
          <div className="card-soft mb-3">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Payload</h2>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className={`wh-btn wh-btn-sm ${aba === 'formatado' ? 'wh-btn-primary' : 'wh-btn-outline'}`}
                  onClick={() => setAba('formatado')}
                  disabled={!evento.corpo_json}
                >
                  JSON
                </button>
                <button
                  type="button"
                  className={`wh-btn wh-btn-sm ${aba === 'cru' ? 'wh-btn-primary' : 'wh-btn-outline'}`}
                  onClick={() => setAba('cru')}
                >
                  Cru
                </button>
                <CopiarTexto
                  texto={aba === 'formatado' && evento.corpo_json ? formatJson(evento.corpo_json) : evento.corpo_texto || ''}
                  rotulo=""
                />
              </div>
            </div>
            <div className="card-soft__body">
              {evento.corpo_encoding === 'base64' && (
                <div className="alert alert-warning py-2 px-3" style={{ fontSize: 12.5 }}>
                  O corpo não era texto UTF-8 válido e foi guardado em base64 — nada foi perdido.
                </div>
              )}
              {!evento.corpo_texto ? (
                <div className="empty-state" style={{ padding: 24 }}>
                  <i className="bi bi-file-earmark" />
                  <div>Requisição sem corpo.</div>
                </div>
              ) : (
                <pre className="codigo-bloco">
                  {aba === 'formatado' && evento.corpo_json ? formatJson(evento.corpo_json) : evento.corpo_texto}
                </pre>
              )}
            </div>
          </div>

          <div className="card-soft mb-3">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Cabeçalhos ({Object.keys(evento.headers || {}).length})</h2>
              {!evento.segredos_visiveis && (
                <span style={{ fontSize: 11.5, color: 'var(--wh-muted)' }}>
                  <i className="bi bi-shield-lock" /> valores sensíveis ocultos para o seu perfil
                </span>
              )}
            </div>
            <div className="wh-table-wrap">
              <table className="wh-table">
                <thead>
                  <tr>
                    <th>Cabeçalho</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(evento.headers || {}).map(([chave, valor]) => (
                    <tr key={chave}>
                      <td className="wh-mono" style={{ whiteSpace: 'nowrap' }}>
                        {chave}
                      </td>
                      <td className="wh-mono" style={{ wordBreak: 'break-all' }}>
                        {valor}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {evento.query_params && Object.keys(evento.query_params).length > 0 && (
            <div className="card-soft mb-3">
              <div className="card-soft__header">
                <h2 className="card-soft__title">Parâmetros de query</h2>
              </div>
              <div className="card-soft__body">
                <pre className="codigo-bloco">{formatJson(evento.query_params)}</pre>
              </div>
            </div>
          )}

          <div className="card-soft">
            <div className="card-soft__header">
              <h2 className="card-soft__title">Encaminhamentos ({evento.entregas?.length || 0})</h2>
            </div>
            {!evento.entregas?.length ? (
              <div className="empty-state" style={{ padding: 24 }}>
                <i className="bi bi-arrow-up-right-circle" />
                <div>Este evento não foi encaminhado.</div>
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      <th>Quando</th>
                      <th>Destino</th>
                      <th>Tentativa</th>
                      <th>Resultado</th>
                      <th>Duração</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evento.entregas.map((entrega) => (
                      <tr key={entrega.id_entrega}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDataHora(entrega.criado_em)}</td>
                        <td className="wh-mono" style={{ wordBreak: 'break-all' }}>
                          {entrega.url_destino}
                        </td>
                        <td>
                          {entrega.tentativa}
                          {entrega.manual && ' (manual)'}
                        </td>
                        <td>
                          {entrega.erro ? (
                            <Pill tone="danger" icon="bi-x-circle" title={entrega.erro}>
                              erro
                            </Pill>
                          ) : (
                            <Pill
                              tone={entrega.status_http >= 200 && entrega.status_http < 300 ? 'success' : 'danger'}
                              icon="bi-arrow-return-right"
                              title={entrega.resposta_resumo}
                            >
                              HTTP {entrega.status_http}
                            </Pill>
                          )}
                        </td>
                        <td>{entrega.duracao_ms} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {reenvioAberto && (
        <Modal titulo="Reenviar evento" onClose={() => setReenvioAberto(false)} largura={520}>
          <p style={{ color: 'var(--wh-ink-2)', fontSize: 13 }}>
            O evento é reenviado exatamente como chegou: mesmo método, mesmos cabeçalhos e o corpo cru.
          </p>
          <label className="wh-label">URL de destino</label>
          <input
            className="wh-input"
            value={urlReenvio}
            onChange={(e) => setUrlReenvio(e.target.value)}
            placeholder="vazio = usar a URL configurada no endpoint"
          />
          {resultadoReenvio && (
            <div
              className={`alert ${resultadoReenvio.ok ? 'alert-success' : 'alert-danger'} py-2 px-3 mt-3`}
              style={{ fontSize: 13 }}
            >
              {resultadoReenvio.texto}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="wh-btn wh-btn-outline" onClick={() => setReenvioAberto(false)}>
              Fechar
            </button>
            <button type="button" className="wh-btn wh-btn-primary" onClick={reenviar} disabled={reenviando}>
              {reenviando ? 'Enviando...' : 'Reenviar agora'}
            </button>
          </div>
        </Modal>
      )}

      {confirmando && (
        <ConfirmarModal
          titulo={confirmando.titulo}
          mensagem={confirmando.mensagem}
          textoBotao="Excluir"
          onConfirmar={confirmando.acao}
          onClose={() => setConfirmando(null)}
        />
      )}
    </div>
  );
}
